import React, { useContext, useEffect, useState } from "react";
import Layout from "../../components/Layout";
import { Link } from "react-router-dom";
import {
  EditIcon,
  DownloadIcon,
  UploadIcon,
  ArrowBottom,
} from "../../assets/icons/Icons";
import SearchFilter from "../../components/filters/SearchFilter";
import { FilterContextProvider } from "../../contexts/FilterContext";
import EditOffice from "../../components/office/Edit";
import CreateOfficeForm from "../../components/office/Create";
import Chip from "../../components/Chip";
import MessagePopup from "../../components/MessagePopup";
import Popup from "../../components/Popup";
import BulkCreateOffices from "../../components/office/bulk-create/BulkCreateOffices";
import DataTable from "../../components/DataTable";
import useData from "../../hooks/useData";
import useExportCSV from "../../hooks/useExportCSV";
import CheckboxFilter from "../../components/filters/CheckboxFilter";
import FilterOffices from "../../api/offices/FilterOffices";
import DropdownButton from "../../components/DropdownButton";
import RadioFilter from "../../components/filters/RadioFilter";
import { useUserContext } from "../../contexts/UserContext";
import ResetFilters from "../../components/filters/ResetFilters";
import { useTranslation } from "react-i18next";
import CustomSelect from "../../components/form/CustomSelect";
import SelectFilter from "../../components/filters/SelectFilter";
import CustomTooltip from "../../components/CustomTooltip";
import { useGlobalContext } from "../../contexts/GlobalContext";
import { isHRB } from "../../utils/whitelabel";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    newItemAdded,
    setNewItemAdded,
    itemUpdated,
    setItemUpdated,
    selectedItem,
    setSelectedItem,
    showEditItem,
    setShowEditItem,
    showCreateItem,
    setShowCreateItem,
  } = useData(FilterOffices);

  const { user: currentUser, can: userCan } = useUserContext();

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Offices"));
    setBreadcrumbs([t("Offices")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const [showBulkCreateOffices, setShowBulkCreateOffices] = useState(false);
  const [officeBulkUploaded, setOfficeBulkUploaded] = useState(false);

  const { handleExportCSV } = useExportCSV(FilterOffices, "offices");

  const handleEditClick = (e, item) => {
    e.preventDefault();
    setSelectedItem(item);
    setShowEditItem(true);
  };

  let tableColumns = [
    { header: isHRB() ? t("Office") : t("Office Name"), accessor: "name" },
    { header: isHRB() ? t("Program ID") : t("External Tag"), accessor: "id" },
    { header: t("Status"), accessor: "status" },
    { header: t("Address 1"), accessor: "address1" },
    { header: t("Address 2"), accessor: "address2" },
    { header: t("City"), accessor: "city" },
    { header: t("Province"), accessor: "state" },
    { header: t("Postal Code"), accessor: "postal_code" },
    { header: t("Country"), accessor: "country" },
    { header: t("Action"), accessor: "actions" },
  ];

  // Remove actions for agent user
  if (currentUser?.role === "agent") {
    tableColumns = tableColumns.filter(
      (column) => column.accessor !== "actions"
    );
  }

  return (
    <Layout title={t("Offices")} breadcrumbs={t("Offices")}>
      {showEditItem && (
        <EditOffice
          office={selectedItem}
          onClose={() => setShowEditItem(false)}
          onUpdate={(e) => {
            setShowEditItem(false);
            setItemUpdated(true);
          }}
        />
      )}
      {itemUpdated && (
        <MessagePopup
          title={t("Office Updated!")}
          message={t("The office has been successfully updated!")}
          onClose={(e) => setItemUpdated(false)}
        />
      )}
      {showCreateItem && (
        <CreateOfficeForm
          onClose={() => setShowCreateItem(false)}
          onCreate={(e) => {
            setShowCreateItem(false);
            setNewItemAdded(true);
          }}
        />
      )}
      {newItemAdded && (
        <MessagePopup
          title={t("Office Created!")}
          message={t("The office has been successfully created!")}
          onClose={(e) => setNewItemAdded(false)}
        />
      )}

      {showBulkCreateOffices && (
        <Popup
          size="extra-large"
          withClose
          withOverlay
          onClose={() => setShowBulkCreateOffices(false)}
        >
          <BulkCreateOffices
            onCreate={(e) => setOfficeBulkUploaded(true)}
            onClose={(e) => setShowBulkCreateOffices(false)}
          />
        </Popup>
      )}

      <DataTable
        filtersTop={
          <FilterContextProvider name="s">
            <SearchFilter />
          </FilterContextProvider>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="status[]">
              <SelectFilter
                options={[
                  {
                    value: "active",
                    label: t("Active"),
                  },
                  {
                    value: "inactive",
                    label: t("Inactive"),
                  },
                ]}
                placeholder={<div>{t("Status")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            <button
              onClick={handleExportCSV}
              className="btn btn-secondary btn-medium"
            >
              <DownloadIcon />
              {t("Export CSV")}
            </button>
            {/*<button onClick={e => setShowBulkCreateOffices(true)} className="btn btn-secondary btn-medium ml-3">*/}
            {/*    <UploadIcon/>*/}
            {/*    Bulk Create Offices*/}
            {/*</button>*/}
            {userCan("office", "write") && (
              <button
                className="btn btn-primary btn-medium"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Create")}
              </button>
            )}
          </>
        }
        columns={tableColumns}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) => (
          <Link to="#" onClick={(e) => handleEditClick(e, item)}>
            <CustomTooltip icon={<EditIcon />} message={t("Edit")} />
          </Link>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "inactive";
              if (value) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "country":
              return value === "124" ? "Canada" : value === "840" ? "USA" : "";
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
